import { FC, useEffect, useState } from 'react';
import { Mail, Calendar, User, Lock, CreditCard, Sparkles, ChevronDown, Package, Building2, Users, Share2, FileText, Target, MessageSquare, CheckCircle2, Circle } from 'lucide-react';
import { TabType } from '../types';

interface ProfileSidebarProps {
    activeTab: TabType;
    setActiveTab: (tab: TabType) => void;
    isInternalUser: boolean;
    wizardData?: any; // strict typing if possible, but any is safe for now
    onTabChange: () => void;
    selectedWizardStep: number | null;
    setSelectedWizardStep: (step: number | null) => void;
}

export const ProfileSidebar: FC<ProfileSidebarProps> = ({
    activeTab,
    setActiveTab,
    isInternalUser,
    wizardData,
    onTabChange,
    selectedWizardStep,
    setSelectedWizardStep,
}) => {
    const [isCompanySetupExpanded, setIsCompanySetupExpanded] = useState(false);

    useEffect(() => {
        if (selectedWizardStep !== null || activeTab === 'wizard') {
            setIsCompanySetupExpanded(true);
        }
    }, [selectedWizardStep, activeTab]);

    const allTabs = [
        { id: 'profile' as TabType, label: 'Profile', icon: User },
        { id: 'email' as TabType, label: 'Email & Password', icon: Lock },
        { id: 'email-config' as TabType, label: 'Email Configuration', icon: Mail },
        { id: 'billing' as TabType, label: 'Billing', icon: CreditCard },
        { id: 'summarizer' as TabType, label: 'Summarizer', icon: FileText },
    ];

    const tabs = allTabs.filter(tab => {
        if (isInternalUser) {
            // Internal/Admin users can only see Profile and Email & Password
            return ['profile', 'email'].includes(tab.id);
        }
        return true;
    });

    const wizardSteps = [
        { stepNumber: 1, label: 'Product/Service', icon: Package },
        { stepNumber: 2, label: 'Company Profile', icon: Building2 },
        { stepNumber: 3, label: 'Competitors', icon: Users },
        { stepNumber: 4, label: 'Social Connections', icon: Share2 },
        { stepNumber: 5, label: 'Content Generation', icon: FileText },
        { stepNumber: 6, label: 'Sales Targets', icon: Target },
        { stepNumber: 7, label: 'Pitch Strategy', icon: MessageSquare },
        { stepNumber: 8, label: 'Connect Calendar', icon: Calendar },
    ];

    const getStepCompletionStatus = (stepNumber: number): boolean => {
        if (!wizardData) return false;
        const data = wizardData;

        switch (stepNumber) {
            case 1:
                return !!(data.productOrService && data.industry);
            case 2:
                return !!(data.logoUrl || (data.keyPeople && data.keyPeople.length > 0));
            case 3:
                return !!(data.competitors && data.competitors.length > 0);
            case 4:
                return !!data.linkedinUrl;
            case 5:
                return !!(data.contentTypes && data.contentTypes.length > 0);
            case 6:
                return !!(data.salesTarget?.name && data.salesTarget?.industry && data.salesTarget?.role);
            case 7:
                return !!data.pitchStrategy;
            case 8:
                return true;
            default:
                return false;
        }
    };

    const handleCompanySetupClick = () => {
        const willExpand = !isCompanySetupExpanded;
        setIsCompanySetupExpanded(willExpand);
        if (!willExpand && selectedWizardStep === null && activeTab === 'wizard') {
            setActiveTab('profile'); // Default back to profile if closing wizard
            onTabChange();
        }
        // Logic from original: 
        // If collapsing and no child is selected, clear wizard tab -> handled above?
        // The original logic:
        // if (!willExpand && selectedWizardStep === null) setActiveTab('profile');
        if (selectedWizardStep !== null) {
            setActiveTab('wizard');
        }
    };

    const handleWizardStepClick = (stepNumber: number) => {
        setActiveTab('wizard');
        setSelectedWizardStep(stepNumber);
        setIsCompanySetupExpanded(true); // Ensure expanded
        onTabChange();
    };

    return (
        <nav className="space-y-1 bg-white rounded-lg border border-gray-200 p-2">
            {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                    <button
                        key={tab.id}
                        onClick={() => {
                            setActiveTab(tab.id);
                            setSelectedWizardStep(null);
                            setIsCompanySetupExpanded(false);
                            onTabChange();
                        }}
                        className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-md transition-colors ${activeTab === tab.id
                            ? 'bg-gray-100 text-gray-900'
                            : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                            }`}
                    >
                        <Icon className="w-5 h-5" />
                        <span>{tab.label}</span>
                    </button>
                );
            })}

            <div className="my-2 border-t border-gray-200" />

            {!isInternalUser && (
                <div>
                    <button
                        onClick={handleCompanySetupClick}
                        className={`w-full flex items-center justify-between px-4 py-3 text-sm font-medium rounded-md transition-colors cursor-pointer ${selectedWizardStep !== null && activeTab === 'wizard'
                            ? 'bg-gray-100 text-gray-900'
                            : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                            }`}
                    >
                        <div className="flex items-center gap-3">
                            <Sparkles className="w-5 h-5" />
                            <span>Company Setup</span>
                        </div>
                        <ChevronDown
                            className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${isCompanySetupExpanded ? 'rotate-180' : ''}`}
                        />
                    </button>

                    <div
                        className={`overflow-hidden transition-all duration-200 ease-out ${isCompanySetupExpanded ? 'max-h-[800px] opacity-100' : 'max-h-0 opacity-0'}`}
                    >
                        <div className="mt-1.5 ml-6 pl-2 space-y-0.5">
                            {wizardSteps.map((step) => {
                                const StepIcon = step.icon;
                                const isCompleted = getStepCompletionStatus(step.stepNumber);
                                const isSelected = selectedWizardStep === step.stepNumber && activeTab === 'wizard';

                                return (
                                    <button
                                        key={step.stepNumber}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleWizardStepClick(step.stepNumber);
                                        }}
                                        className={`group w-full flex items-center gap-3 px-3 py-2 rounded-md transition-all duration-150 cursor-pointer relative ${isSelected
                                            ? 'bg-gray-100 text-gray-900'
                                            : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                                            }`}
                                    >
                                        {isSelected && (
                                            <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-gray-900 rounded-r-full" />
                                        )}
                                        <div className={`flex-shrink-0 w-6 h-6 rounded-md flex items-center justify-center transition-colors ${isSelected
                                            ? 'bg-gray-200 text-gray-700'
                                            : 'bg-gray-100 text-gray-500 group-hover:bg-gray-200 group-hover:text-gray-700'
                                            }`}>
                                            <StepIcon className="w-3.5 h-3.5" />
                                        </div>
                                        <span className={`flex-1 text-left text-[13px] ${isSelected ? 'font-medium' : 'font-normal'}`}>
                                            {step.label}
                                        </span>
                                        <div className="flex-shrink-0">
                                            {isCompleted ? (
                                                <div className="flex items-center justify-center w-4 h-4 rounded-full bg-green-50">
                                                    <CheckCircle2 className="w-3 h-3 text-green-600" />
                                                </div>
                                            ) : (
                                                <div className="flex items-center justify-center w-4 h-4 rounded-full border border-gray-200 bg-white">
                                                    <Circle className="w-2 h-2 text-gray-300" />
                                                </div>
                                            )}
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div >
            )}
        </nav >
    );
};
